'use client';

import { useId, useState, type ReactNode } from 'react';

import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/** Values listed before „Zobraziť viac (+N)“ (Figma 60093:4669). */
const VISIBLE_VALUES = 5;

export type FilterValue = { id: number; name: string };

/** Colour swatch in place of the box: border/default ring, a selected one gets a brand ring. */
const SWATCH =
  'rounded-full border-[#D5D8DC] data-[state=checked]:border-white data-[state=checked]:ring-2 data-[state=checked]:ring-[#D7191F]';

/**
 * One collapsible group of the drawer (Figma „Filter · …“): DLZ/Heading/H5 title with a red caret
 * that toggles the values, 24 px below them a border/subtle line.
 */
export function FilterGroup({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();

  return (
    <div className="flex flex-col gap-4 border-b border-[#E2E4E7] pb-6">
      <h3 className="dlz-h5 text-[#0F1012]">
        <button
          type="button"
          id={`${id}-title`}
          aria-expanded={open}
          aria-controls={open ? `${id}-values` : undefined}
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2"
        >
          <span className="min-w-0 flex-1">{title}</span>
          <Icon name={open ? 'caret-up' : 'caret-down'} size={20} className="text-[#D7191F]" />
        </button>
      </h3>
      {open ? (
        <div
          id={`${id}-values`}
          role="group"
          aria-labelledby={`${id}-title`}
          className="flex flex-col gap-4"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Checkbox per value (Figma „Checkbox“, DLZ/Body/M label); long lists start with five values.
 * `color` turns the box into a round swatch (colour attributes).
 */
export function CheckList<T extends FilterValue>({
  values,
  isChecked,
  onToggle,
  color,
}: {
  values: T[];
  isChecked: (id: number) => boolean;
  onToggle: (value: T) => void;
  color?: (value: T) => string | undefined;
}) {
  const { t } = useTranslations();
  const listId = useId();
  const more = values.length - VISIBLE_VALUES;
  // A checked value must not hide behind „Zobraziť viac“.
  const [expanded, setExpanded] = useState(() =>
    values.slice(VISIBLE_VALUES).some(value => isChecked(value.id)),
  );

  return (
    <>
      <ul id={listId} className="flex flex-col gap-3">
        {(expanded ? values : values.slice(0, VISIBLE_VALUES)).map(value => (
          <li key={value.id}>
            <label className="flex cursor-pointer items-center gap-3 text-[#0F1012]">
              <Checkbox
                checked={isChecked(value.id)}
                onCheckedChange={() => onToggle(value)}
                {...(color && {
                  style: { background: color(value) },
                  className: SWATCH,
                  iconClassName: 'drop-shadow-[0_0_1px_#0F1012]',
                })}
              />
              <span className="dlz-body-m">{value.name}</span>
            </label>
          </li>
        ))}
      </ul>
      {more > 0 ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(!expanded)}
          className="dlz-body-s-medium self-start rounded-md text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2"
        >
          {expanded ? t('text604') : `${t('text338')} (+${more})`}
        </button>
      ) : null}
    </>
  );
}

/** Values in the administration order. */
export function byPosition<T extends { position?: number }>(values: T[] | null | undefined) {
  return [...(values ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}
